import type { Issue } from './types';

/**
 * HOW A CHECKLIST MAY MOVE A CARD ALONG — the board's checklist-prompt settings.
 *
 * A card's own checklist (issue.checklist) can move the card as its boxes are
 * ticked, two ways:
 *
 *   · ON THE FIRST TICK of a To-do card, offer to START it — move it to In
 *     Progress. Ticking the first step is the moment work actually began, and a
 *     board where that card sits in To-do is a board whose columns lie.
 *   · ON THE LAST TICK — every item done, 100% — offer to FINISH it, move it to
 *     Done. A fully-worked checklist is the truest read that the card is complete
 *     (progress-derive.ts), truer than the shared Definition of Done, which
 *     "will not get used much" (Vinnie, 28 Oct). So a 100% checklist is ALSO a
 *     valid completion that clears the Done gate even when the DoD is unticked —
 *     see `doneAllowed` in done.ts.
 *
 * Both are OFFERS — a confirm the person accepts or declines — never a silent
 * automatic move.
 *
 * The shape is deliberately a MASTER switch plus a flag PER KIND of move, so more
 * kinds of this prompt can be added later under the one switch rather than growing
 * a new switch each time (Vinnie, 28 Oct). An absent or null value on a board means
 * the defaults below, so a board that has never been asked behaves identically on
 * every surface rather than differently on whichever one forgot the fallback.
 *
 * Pure: no store, no React — unit-tested in scripts/test-checklist-prompts.ts.
 */
export interface ChecklistPrompts {
  /** The master: may a checklist move the card at all. Off = neither offer fires
   *  and a checklist no longer clears the Done gate (the DoD stands alone). */
  on: boolean;
  /** When the FIRST box is ticked on a To-do card, offer to move it to In Progress. */
  onFirstTick: boolean;
  /** When the LAST box is ticked (100%), offer to move the card to Done — and let a
   *  100% checklist clear the Done gate in place of the board DoD. */
  onComplete: boolean;
}

/**
 * THE PROMPTS A BOARD STARTS WITH, until it says otherwise — everything on.
 *
 * Out of the box a checklist moves the card along both ways: "checklist 100%
 * completes it" (Vinnie, 28 Oct) is the default behaviour, and the first tick
 * starts a parked card. An admin who wants the board's Definition of Done to stand
 * as the sole hard gate turns the master switch off.
 */
export const DEFAULT_CHECKLIST_PROMPTS: ChecklistPrompts = {
  on: true,
  onFirstTick: true,
  onComplete: true,
};

/**
 * THE BOARD'S EFFECTIVE PROMPTS — its stored override, or the defaults. One place,
 * so no caller re-derives the fallback and gets it subtly different.
 */
export function checklistPromptsOf(
  stored: ChecklistPrompts | null | undefined,
): ChecklistPrompts {
  return stored ?? DEFAULT_CHECKLIST_PROMPTS;
}

/**
 * IS THE CARD'S OWN CHECKLIST FULLY TICKED — every item done, with at least one
 * item. An absent or empty checklist is "no checklist" and this is false: such a
 * card falls through to the board DoD exactly as before. Pure predicate, shared by
 * the offer trigger and the Done gate so they can never disagree about which cards.
 */
export function checklistComplete(issue: Pick<Issue, 'checklist'>): boolean {
  const list = issue.checklist;
  return !!list && list.length > 0 && list.every((it) => it.done);
}
